import { useEffect, useMemo, useRef, useState } from 'react';
import {
  FaCheck,
  FaCircleInfo,
  FaDatabase,
  FaDownload,
  FaFileExcel,
  FaFilePdf,
  FaTriangleExclamation,
  FaUpload,
} from 'react-icons/fa6';

import Modal from '../../components/ui/Modal';
import StatusMessage from '../../components/ui/StatusMessage';
import { useAuth } from '../../context/AuthContext';
import {
  getFullBackupData,
  getSettingsExportData,
  getSystemInformation,
  recordLastBackupDate,
} from '../../services/settingsDataService';
import {
  EXPORT_DATASETS,
  areAllExportDatasetsSelected,
  buildBackupFilename,
  buildBackupPayload,
  buildExportFilename,
  buildExportPayload,
  buildRestoreValidation,
  createExcelBlob,
  createPdfBlob,
  createRestoreExecutionPlan,
  downloadBlob,
  parseBackupJson,
  setExportDatasetSelected,
  toggleAllExportDatasets,
} from '../../services/settingsExportService';
import {
  logSystemAuditEvent,
  restoreBackup,
} from '../../services/settingsRestoreService';

const datasetSummaryLabels = {
  suppliers: 'suppliers',
  supplierContacts: 'supplier contacts',
  products: 'products',
  categories: 'categories',
  users: 'users',
  productVariants: 'product variants',
  priceHistory: 'price history records',
  productSuppliers: 'product supplier relations',
};

const initialSystemInfo = {
  version: '-',
  firebaseProjectId: '-',
  environment: '-',
  userCount: 0,
  supplierCount: 0,
  productCount: 0,
  categoryCount: 0,
  lastBackupDate: '',
};

const formatDateTime = (value) => {
  if (!value) {
    return 'Not available';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Not available';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

function SectionTitle({ icon: Icon, title, caption }) {
  return (
    <div className="section-header settings-section-header">
      <div className="settings-section-title">
        <span className="settings-section-icon" aria-hidden="true">
          <Icon />
        </span>
        <div>
          <h2>{title}</h2>
          <p>{caption}</p>
        </div>
      </div>
    </div>
  );
}

function SummaryList({ summary }) {
  const entries = Object.entries(summary || {});

  if (entries.length === 0) {
    return null;
  }

  return (
    <dl className="settings-summary-list">
      {entries.map(([key, value]) => (
        <div key={key} className="settings-summary-item">
          <dt>{datasetSummaryLabels[key] || key}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function SettingsPage() {
  const fileInputRef = useRef(null);
  const { user, role, isOwner, isAdmin } = useAuth();
  const [selectedDatasets, setSelectedDatasets] = useState(
    EXPORT_DATASETS.map((dataset) => dataset.key)
  );
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [systemInfo, setSystemInfo] = useState(initialSystemInfo);
  const [isSystemInfoLoading, setIsSystemInfoLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [pageMessage, setPageMessage] = useState('');
  const [pageError, setPageError] = useState('');
  const [restorePayload, setRestorePayload] = useState(null);
  const [restoreValidation, setRestoreValidation] = useState(null);
  const [restorePlan, setRestorePlan] = useState(null);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [restoreConfirmed, setRestoreConfirmed] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  const exportedBy = user?.email || 'Unknown user';
  const allSelected = useMemo(
    () => areAllExportDatasetsSelected(selectedDatasets),
    [selectedDatasets]
  );
  const restoreErrors = restoreValidation?.errors || [];
  const isRestoreReady = Boolean(isOwner && restoreValidation?.isValid);
  const canUseSettingsActions = Boolean(isAdmin);

  const loadSystemInformation = async () => {
    setIsSystemInfoLoading(true);

    try {
      const data = await getSystemInformation();
      setSystemInfo(data);
    } catch (error) {
      console.error('SETTINGS SYSTEM INFO ERROR:', error);
      setPageError('Failed to load system information.');
    } finally {
      setIsSystemInfoLoading(false);
    }
  };

  useEffect(() => {
    loadSystemInformation();
  }, []);

  const handleDatasetChange = (datasetKey, isSelected) => {
    setSelectedDatasets((currentKeys) =>
      setExportDatasetSelected(currentKeys, datasetKey, isSelected)
    );
  };

  const handleExport = async () => {
    if (!canUseSettingsActions) {
      setPageError('Only owner and admin accounts can export data.');
      setPageMessage('');
      return;
    }

    if (selectedDatasets.length === 0) {
      setPageError('Select at least one dataset to export.');
      setPageMessage('');
      return;
    }

    setIsExporting(true);
    setPageError('');
    setPageMessage('');

    try {
      const data = await getSettingsExportData(selectedDatasets);
      const payload = buildExportPayload(data, selectedDatasets, { exportedBy });
      const blob = exportFormat === 'pdf'
        ? createPdfBlob(payload)
        : createExcelBlob(payload);

      downloadBlob(blob, buildExportFilename(exportFormat));
      logSystemAuditEvent('system.export_generated', {
        format: exportFormat,
        datasets: selectedDatasets,
        datasetCount: selectedDatasets.length,
      }).catch((auditError) => {
        console.error('SETTINGS EXPORT AUDIT ERROR:', auditError);
      });
      setPageMessage('Export file generated.');
    } catch (error) {
      console.error('SETTINGS EXPORT ERROR:', error);
      setPageError('Failed to generate export.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleCreateBackup = async () => {
    if (!canUseSettingsActions) {
      setPageError('Only owner and admin accounts can create backups.');
      setPageMessage('');
      return;
    }

    setIsBackingUp(true);
    setPageError('');
    setPageMessage('');

    try {
      const data = await getFullBackupData();
      const createdAt = new Date().toISOString();
      const payload = buildBackupPayload(data, { createdAt, exportedBy });
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: 'application/json',
      });

      downloadBlob(blob, buildBackupFilename(createdAt));
      recordLastBackupDate(createdAt);
      logSystemAuditEvent('system.backup_created', {
        backupVersion: payload.version,
        createdAt,
        collectionCount: Object.keys(payload).filter((key) =>
          Array.isArray(payload[key])
        ).length,
      }).catch((auditError) => {
        console.error('SETTINGS BACKUP AUDIT ERROR:', auditError);
      });
      setSystemInfo((currentInfo) => ({
        ...currentInfo,
        lastBackupDate: createdAt,
      }));
      setPageMessage('Full backup file generated.');
    } catch (error) {
      console.error('SETTINGS BACKUP ERROR:', error);
      setPageError('Failed to create backup.');
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleRestoreFile = (event) => {
    const file = event.target.files?.[0];

    setRestorePayload(null);
    setRestoreValidation(null);
    setRestorePlan(null);
    setRestoreConfirmed(false);
    setPageMessage('');
    setPageError('');

    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const result = parseBackupJson(String(reader.result || ''));
      const validation = result.payload
        ? buildRestoreValidation(result.payload, role)
        : result.validation;

      setRestorePayload(result.payload);
      setRestoreValidation(validation);

      if (!validation.isValid) {
        setPageError('Backup validation failed.');
      }
    };
    reader.onerror = () => {
      setPageError('Failed to read backup file.');
    };
    reader.readAsText(file);
  };

  const handleReviewRestore = () => {
    const plan = createRestoreExecutionPlan(restorePayload, role);

    setRestorePlan(plan);
    setRestoreConfirmed(false);
    setIsRestoreModalOpen(true);
  };

  const handleConfirmRestore = async () => {
    if (!restorePlan?.validation?.isValid || !restoreConfirmed || !restorePayload) {
      return;
    }

    setIsRestoring(true);
    setPageError('');
    setPageMessage('');

    try {
      const result = await restoreBackup(restorePayload);
      setIsRestoreModalOpen(false);
      setRestoreConfirmed(false);
      setRestorePayload(null);
      setRestoreValidation(null);
      setRestorePlan(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      await loadSystemInformation();
      setPageMessage(
        `Restore completed. ${result?.documentCount || 0} documents restored.`
      );
    } catch (error) {
      console.error('SETTINGS RESTORE ERROR:', error);
      setPageError(error.message || 'Failed to restore backup.');
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <div className="page settings-page">
      <div className="page-header settings-page-header">
        <div className="page-title-block">
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">
            Export operational data, create local backups, and review system status.
          </p>
        </div>
      </div>

      <StatusMessage tone="success" message={pageMessage} />
      <StatusMessage tone="error" message={pageError} />

      <section className="section settings-card">
        <SectionTitle
          icon={FaDownload}
          title="Export Data"
          caption="Generate Excel or PDF files from selected collections."
        />

        <div className="settings-export-layout">
          <div className="settings-dataset-panel">
            <div className="settings-select-all-row">
              <label className="settings-checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={() =>
                    setSelectedDatasets((currentKeys) =>
                      toggleAllExportDatasets(currentKeys)
                    )
                  }
                />
                <span>Select All</span>
              </label>
              <span>{selectedDatasets.length} selected</span>
            </div>

            <div className="settings-dataset-grid">
              {EXPORT_DATASETS.map((dataset) => (
                <label key={dataset.key} className="settings-checkbox settings-dataset-option">
                  <input
                    type="checkbox"
                    checked={selectedDatasets.includes(dataset.key)}
                    onChange={(event) =>
                      handleDatasetChange(dataset.key, event.target.checked)
                    }
                  />
                  <span>{dataset.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="settings-format-panel">
            <div className="settings-format-group" role="radiogroup" aria-label="Export format">
              <label className={`settings-format-option${exportFormat === 'xlsx' ? ' active' : ''}`}>
                <input
                  type="radio"
                  name="export-format"
                  value="xlsx"
                  checked={exportFormat === 'xlsx'}
                  onChange={() => setExportFormat('xlsx')}
                />
                <FaFileExcel aria-hidden="true" />
                <span>Excel (.xlsx)</span>
              </label>
              <label className={`settings-format-option${exportFormat === 'pdf' ? ' active' : ''}`}>
                <input
                  type="radio"
                  name="export-format"
                  value="pdf"
                  checked={exportFormat === 'pdf'}
                  onChange={() => setExportFormat('pdf')}
                />
                <FaFilePdf aria-hidden="true" />
                <span>PDF (.pdf)</span>
              </label>
            </div>

            <button
              type="button"
              className="button button-primary settings-action-button"
              onClick={handleExport}
              disabled={
                !canUseSettingsActions ||
                isExporting ||
                selectedDatasets.length === 0
              }
            >
              <FaDownload aria-hidden="true" />
              {isExporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>
      </section>

      <section className="section settings-card">
        <SectionTitle
          icon={FaDatabase}
          title="Backup & Restore"
          caption="Create local full backups and validate restore files before execution."
        />

        <div className="settings-backup-grid">
          <article className="settings-subcard">
            <h3>Create Backup</h3>
            <p>
              Downloads a full JSON backup containing categories, suppliers,
              supplier contacts, products, product variants, price history, and
              product supplier relations.
            </p>
            <button
              type="button"
              className="button button-primary settings-action-button"
              onClick={handleCreateBackup}
              disabled={!canUseSettingsActions || isBackingUp}
            >
              <FaDatabase aria-hidden="true" />
              {isBackingUp ? 'Creating Backup...' : 'Create Full Backup'}
            </button>
          </article>

          <article className="settings-subcard">
            <div className="settings-restore-header">
              <h3>Restore</h3>
              {isOwner ? (
                <span className="settings-status-pill settings-status-pill--success">
                  Owner access
                </span>
              ) : (
                <span className="settings-status-pill settings-status-pill--warning">
                  Owner only
                </span>
              )}
            </div>
            <p>
              Upload a backup file to validate format, version, and required
              collections. Restores replace supported collections through a
              callable function.
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              className="settings-file-input"
              onChange={handleRestoreFile}
              disabled={!isOwner}
            />
            <button
              type="button"
              className="button settings-action-button"
              onClick={() => fileInputRef.current?.click()}
              disabled={!isOwner}
            >
              <FaUpload aria-hidden="true" />
              Upload Backup File
            </button>

            {!isOwner ? (
              <StatusMessage
                tone="info"
                message="Only owner accounts can validate and restore backup files."
              />
            ) : null}

            {restoreErrors.length > 0 ? (
              <div className="settings-validation-box settings-validation-box--error">
                <FaTriangleExclamation aria-hidden="true" />
                <div>
                  <strong>Validation failed</strong>
                  <ul>
                    {restoreErrors.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : null}

            {restoreValidation?.isValid ? (
              <div className="settings-validation-box settings-validation-box--success">
                <FaCheck aria-hidden="true" />
                <div>
                  <strong>Backup validated</strong>
                  <SummaryList summary={restoreValidation.summary} />
                  <button
                    type="button"
                    className="button button-primary"
                    onClick={handleReviewRestore}
                    disabled={!isRestoreReady}
                  >
                    Review Restore
                  </button>
                </div>
              </div>
            ) : null}
          </article>
        </div>
      </section>

      <section className="section settings-card">
        <SectionTitle
          icon={FaCircleInfo}
          title="System Information"
          caption="Current deployment and record totals."
        />

        <dl className="settings-info-grid">
          <div>
            <dt>Vacanza OS version</dt>
            <dd>{isSystemInfoLoading ? 'Loading...' : systemInfo.version}</dd>
          </div>
          <div>
            <dt>Firebase project id</dt>
            <dd>{isSystemInfoLoading ? 'Loading...' : systemInfo.firebaseProjectId}</dd>
          </div>
          <div>
            <dt>Environment</dt>
            <dd>{isSystemInfoLoading ? 'Loading...' : systemInfo.environment}</dd>
          </div>
          <div>
            <dt>User count</dt>
            <dd>{isSystemInfoLoading ? '--' : systemInfo.userCount}</dd>
          </div>
          <div>
            <dt>Supplier count</dt>
            <dd>{isSystemInfoLoading ? '--' : systemInfo.supplierCount}</dd>
          </div>
          <div>
            <dt>Product count</dt>
            <dd>{isSystemInfoLoading ? '--' : systemInfo.productCount}</dd>
          </div>
          <div>
            <dt>Category count</dt>
            <dd>{isSystemInfoLoading ? '--' : systemInfo.categoryCount}</dd>
          </div>
          <div>
            <dt>Last backup date</dt>
            <dd>{isSystemInfoLoading ? 'Loading...' : formatDateTime(systemInfo.lastBackupDate)}</dd>
          </div>
        </dl>
      </section>

      <Modal
        isOpen={isRestoreModalOpen}
        onClose={() => setIsRestoreModalOpen(false)}
        onConfirm={handleConfirmRestore}
        title="Confirm Restore"
        confirmLabel={isRestoring ? 'Restoring...' : 'Restore Database'}
        confirmDisabled={
          isRestoring || !restoreConfirmed || !restorePlan?.validation?.isValid
        }
        maxWidth={680}
        confirmButtonStyle={{
          backgroundColor: '#b91c1c',
          borderColor: '#b91c1c',
          color: '#ffffff',
        }}
      >
        <div className="settings-restore-modal">
          <StatusMessage
            tone="error"
            message="Restore will overwrite supported collection documents from this backup, remove records missing from it after writes succeed, and attempt rollback if execution fails."
          />
          <SummaryList summary={restorePlan?.validation?.summary} />
          <div className="settings-final-step">
            <strong>Final restore execution</strong>
            <p>{restorePlan?.finalStep}</p>
          </div>
          <label className="settings-checkbox">
            <input
              type="checkbox"
              checked={restoreConfirmed}
              disabled={isRestoring}
              onChange={(event) => setRestoreConfirmed(event.target.checked)}
            />
            <span>I reviewed the restore summary and understand supported collection data will be replaced by this backup.</span>
          </label>
        </div>
      </Modal>
    </div>
  );
}
